// KnowledgeBase Callouts plugin
// Transforms Obsidian-style callouts inside blockquotes:
// > [!note] Title
// > Content
// Also supports [!type]+ (expanded) and [!type]- (collapsed) markers.

import { registerMarkdownPostProcessor } from '../../services/PluginAPI.js'
import './styles/callouts.css'

// Configure whether to support paragraph-based callouts (without '>').
// Blockquote style is the default and recommended; set to true to re-enable paragraph support.
const PARAGRAPH_SUPPORT = false

const TYPE_MAP = {
  note: 'info',
  info: 'info',
  tip: 'tip',
  hint: 'tip',
  important: 'tip',
  success: 'success',
  check: 'success',
  question: 'question',
  help: 'question',
  warning: 'warning',
  caution: 'warning',
  attention: 'warning',
  failure: 'failure',
  fail: 'failure',
  error: 'failure',
  danger: 'failure',
  bug: 'bug',
  example: 'example',
  quote: 'quote'
}

function parseCalloutHeader(text) {
  // Matches [!type][+/-]? optional space, optional title
  // Only consider the first visual line so multi-line paragraphs (with <br>) still match.
  const raw = String(text || '')
  const firstLine = raw.split(/\r?\n/)[0].trim()
  const m = /^\[!([a-zA-Z0-9_-]+)\]\s*([+-])?\s*(.*)$/.exec(firstLine)
  if (!m) return null
  const rawType = (m[1] || '').toLowerCase()
  const type = TYPE_MAP[rawType] || rawType
  const fold = m[2] || '' // '+' expanded, '-' collapsed
  const title = (m[3] || '').trim()
  return { type, fold, title }
}

function upgradeBlockquote(quote) {
  // Expect the first child paragraph to contain the marker
  const firstP = quote.querySelector('p')
  if (!firstP) return false
  const text = firstP.textContent || ''
  const meta = parseCalloutHeader(text)
  if (!meta) return false

  // Build container
  quote.classList.add('kb-callout', `kb-callout-${meta.type}`)
  quote.setAttribute('data-callout', meta.type)
  if (meta.fold === '-') quote.classList.add('is-collapsed')
  if (meta.fold === '+') quote.classList.add('is-expanded')

  // Remove only the header marker from first paragraph's HTML, preserve the rest (including <br>)
  const markerRe = /^\s*\[![A-Za-z0-9_-]+\]\s*[+-]?\s*/
  const firstHtml = firstP.innerHTML || ''
  const remainingHtml = firstHtml.replace(markerRe, '')
  // Split the remaining HTML into header (first visual line) and body (rest)
  const splitOnBr = remainingHtml.split(/<br\s*\/?>(.*)/is)
  let headerHtml = ''
  let bodyHtml = ''
  if (splitOnBr && splitOnBr.length > 1) {
    headerHtml = splitOnBr[0]
    bodyHtml = splitOnBr[1]
  } else {
    headerHtml = remainingHtml.trim()
    bodyHtml = ''
  }

  const header = document.createElement('div')
  header.className = 'kb-callout-title'
  header.innerHTML = headerHtml || meta.title || meta.type.toUpperCase()

  const body = document.createElement('div')
  body.className = 'kb-callout-body'

  // Toggle collapse/expand on header click
  header.addEventListener('click', () => {
    if (quote.classList.contains('is-collapsed')) {
      quote.classList.remove('is-collapsed')
      quote.classList.add('is-expanded')
    } else if (quote.classList.contains('is-expanded')) {
      quote.classList.remove('is-expanded')
      quote.classList.add('is-collapsed')
    } else {
      // default toggle if neither set
      quote.classList.add('is-collapsed')
    }
  })

  // Move remaining children (including paragraphs after the first) into body
  const toMove = []
  for (const child of Array.from(quote.childNodes)) {
    if (child === firstP) continue
    toMove.push(child)
  }
  // Remove all children, then append header/body
  quote.innerHTML = ''
  quote.appendChild(header)
  quote.appendChild(body)
  if (bodyHtml && bodyHtml.trim()) {
    const p = document.createElement('p')
    p.innerHTML = bodyHtml
    body.appendChild(p)
  }
  toMove.forEach((n) => body.appendChild(n))

  return true
}

export function registerCalloutsPlugin() {
  // Post processor over the entire rendered note
  registerMarkdownPostProcessor(
    (root) => {
      // 1) Upgrade blockquotes that contain callout markers
      const blockquotes = Array.from(root.querySelectorAll('blockquote'))
      for (const q of blockquotes) {
        try {
          if (upgradeBlockquote(q)) continue
        } catch {
          /* noop */
        }
      }

      // 2) Paragraph callouts are disabled by default
      if (PARAGRAPH_SUPPORT) {
        const paras = Array.from(root.querySelectorAll('p'))
        const startsWithCallout = (el) => {
          const t = (el.textContent || '').trim()
          return /^\[![a-zA-Z0-9_-]+\]/.test(t)
        }
        const isEndMarker = (el) => {
          const t = (el.textContent || '').trim().toLowerCase()
          return t === '[!end]'
        }
        const upgradeParagraph = (p) => {
          const meta = parseCalloutHeader(p.textContent || '')
          if (!meta) return false

          const container = document.createElement('div')
          container.className = `kb-callout kb-callout-${meta.type}`
          container.setAttribute('data-callout', meta.type)
          if (meta.fold === '-') container.classList.add('is-collapsed')
          if (meta.fold === '+') container.classList.add('is-expanded')

          const header = document.createElement('div')
          header.className = 'kb-callout-title'
          header.textContent = meta.title || meta.type.toUpperCase()

          const body = document.createElement('div')
          body.className = 'kb-callout-body'

          header.addEventListener('click', () => {
            if (container.classList.contains('is-collapsed')) {
              container.classList.remove('is-collapsed')
              container.classList.add('is-expanded')
            } else if (container.classList.contains('is-expanded')) {
              container.classList.remove('is-expanded')
              container.classList.add('is-collapsed')
            } else {
              container.classList.add('is-collapsed')
            }
          })

          const markerRe = /^\s*\[![A-Za-z0-9_-]+\]\s*[+-]?\s*/
          const contentHtml = (p.innerHTML || '').replace(markerRe, '')
          const splitOnBr = contentHtml.split(/<br\s*\/?>(.*)/is)
          let headerHtml = ''
          let bodyHtml = ''
          if (splitOnBr && splitOnBr.length > 1) {
            headerHtml = splitOnBr[0]
            bodyHtml = splitOnBr[1]
          } else {
            headerHtml = contentHtml.trim()
            bodyHtml = ''
          }

          p.parentElement.insertBefore(container, p)
          p.remove()

          header.innerHTML = headerHtml || meta.title || meta.type.toUpperCase()
          container.appendChild(header)
          container.appendChild(body)

          if (bodyHtml && bodyHtml.trim()) {
            const firstBody = document.createElement('p')
            firstBody.innerHTML = bodyHtml
            body.appendChild(firstBody)
          }

          let next = container.nextElementSibling
          while (next && next.tagName === 'P') {
            if (startsWithCallout(next)) break
            if (isEndMarker(next)) {
              next.remove()
              break
            }
            const toMove = next
            next = next.nextElementSibling
            body.appendChild(toMove)
          }
          return true
        }

        for (const p of paras) {
          try {
            if (startsWithCallout(p)) upgradeParagraph(p)
          } catch {
            /* noop */
          }
        }
      }
    },
    { sortOrder: 50 }
  )
}

export default { registerCalloutsPlugin }
